import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './TripDetailsPage.css';

const TripDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get trip data from navigation state - REQUIRED, no fallback
  const trip = location.state?.trip;

  // Redirect if no trip data
  React.useEffect(() => {
    if (!trip) {
      console.error('No trip data found. Redirecting to dashboard...');
      navigate('/dashboard', { 
        replace: true,
        state: { error: 'No trip information available' }
      });
    }
  }, [trip, navigate]);

  return (
    <div className="trip-details-page">
      <Header />
      
      {/* Trip Summary Hero */}
      <section className="trip-hero-section">
        <div className="page-container">
          <div className="trip-hero-card">
            <div className="trip-hero-header">
              <div className="trip-airline-hero">
                <span className="trip-logo-hero">{trip.airlineLogo}</span>
                <div>
                  <h1 className="trip-airline-name-hero">{trip.airline}</h1>
                  <p className="trip-route-hero">
                    {trip.departureCity} ({trip.departureAirport}) → {trip.arrivalCity} ({trip.arrivalAirport})
                  </p>
                </div>
              </div>
              <div className="trip-status-hero">
                <span className={`status-badge-hero ${trip.status.toLowerCase()}`}>
                  {trip.status}
                </span>
              </div>
            </div>

            <div className="trip-flight-times">
              <div className="time-block-hero">
                <span className="time-value-hero">{trip.departureTime}</span>
                <span className="time-date-hero">{trip.departureDate}</span>
                <span className="airport-code-hero">{trip.departureAirport}</span>
                <span className="city-name-hero">{trip.departureCity}</span>
              </div>

              <div className="flight-connector-hero">
                <div className="connector-icon-wrapper">
                  <span className="connector-icon-hero">✈️</span>
                </div>
                <div className="connector-info">
                  <span className="connector-duration-hero">{trip.duration}</span>
                  <span className="connector-stops-hero">{trip.stops}</span>
                </div>
              </div>

              <div className="time-block-hero">
                <span className="time-value-hero">{trip.arrivalTime}</span>
                <span className="time-date-hero">{trip.arrivalDate}</span>
                <span className="airport-code-hero">{trip.arrivalAirport}</span>
                <span className="city-name-hero">{trip.arrivalCity}</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="trip-details-main">
        <div className="page-container">
          <div className="details-layout">
            {/* Left Column */}
            <div className="details-left">
              {/* Passenger Information */}
              <div className="detail-card-section">
                <div className="detail-card">
                  <div className="detail-card-header">
                    <span className="detail-icon">👤</span>
                    <h2 className="detail-title">Passenger Details</h2>
                  </div>
                  <div className="detail-card-body">
                    {trip.passengers?.map((passenger, index) => (
                      <div key={index} className="passenger-detail-item">
                        <div className="passenger-info-row">
                          <div className="passenger-name-section">
                            <h3 className="passenger-name">{passenger.name}</h3>
                            <span className="passenger-type-badge">{passenger.type}</span>
                          </div>
                          {passenger.seat && (
                            <div className="passenger-seat">
                              <span className="seat-label">Seat</span>
                              <span className="seat-number">{passenger.seat}</span>
                            </div>
                          )}
                        </div>
                        <div className="passenger-details-grid">
                          <div className="passenger-detail">
                            <span className="detail-label">Ticket Reference</span>
                            <span className="detail-value">{passenger.ticketRef}</span>
                          </div>
                          {passenger.nationality && (
                            <div className="passenger-detail">
                              <span className="detail-label">Nationality</span>
                              <span className="detail-value">{passenger.nationality}</span>
                            </div>
                          )}
                          {passenger.gender && (
                            <div className="passenger-detail">
                              <span className="detail-label">Gender</span>
                              <span className="detail-value">{passenger.gender}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Flight Details */}
              <div className="detail-card-section">
                <div className="detail-card">
                  <div className="detail-card-header">
                    <span className="detail-icon">✈️</span>
                    <h2 className="detail-title">Flight Details</h2>
                  </div>
                  <div className="detail-card-body">
                    <div className="flight-details-grid">
                      <div className="flight-detail-item">
                        <span className="detail-icon-small">🔢</span>
                        <div className="detail-content">
                          <span className="detail-label">Flight Number</span>
                          <span className="detail-value">{trip.flightNumber}</span>
                        </div>
                      </div>
                      <div className="flight-detail-item">
                        <span className="detail-icon-small">✈️</span>
                        <div className="detail-content">
                          <span className="detail-label">Aircraft Type</span>
                          <span className="detail-value">{trip.aircraftType}</span>
                        </div>
                      </div>
                      <div className="flight-detail-item">
                        <span className="detail-icon-small">🚪</span>
                        <div className="detail-content">
                          <span className="detail-label">Terminal</span>
                          <span className="detail-value">{trip.terminal}</span>
                        </div>
                      </div>
                      <div className="flight-detail-item">
                        <span className="detail-icon-small">📍</span>
                        <div className="detail-content">
                          <span className="detail-label">Gate</span>
                          <span className="detail-value">{trip.gate}</span>
                        </div>
                      </div>
                      <div className="flight-detail-item">
                        <span className="detail-icon-small">💺</span>
                        <div className="detail-content">
                          <span className="detail-label">Cabin Class</span>
                          <span className="detail-value">{trip.travelClass}</span>
                        </div>
                      </div>
                      <div className="flight-detail-item">
                        <span className="detail-icon-small">⏰</span>
                        <div className="detail-content">
                          <span className="detail-label">Boarding Time</span>
                          <span className="detail-value">{trip.boardingTime}</span>
                        </div>
                      </div>
                    </div>

                    <div className="baggage-section">
                      <h3 className="baggage-title">Baggage Allowance</h3>
                      <div className="baggage-details">
                        <div className="baggage-item">
                          <span className="baggage-icon">🎒</span>
                          <div className="baggage-info">
                            <span className="baggage-label">Carry-on</span>
                            <span className="baggage-value">{trip.baggage?.carryOn || '1 piece (7kg)'}</span>
                          </div>
                        </div>
                        <div className="baggage-item">
                          <span className="baggage-icon">🧳</span>
                          <div className="baggage-info">
                            <span className="baggage-label">Checked Baggage</span>
                            <span className="baggage-value">{trip.baggage?.checked || '2 pieces (23kg each)'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="meal-section">
                      <div className="meal-item">
                        <span className="meal-icon">🍽️</span>
                        <div className="meal-info">
                          <span className="meal-label">Meal Service</span>
                          <span className="meal-value">{trip.meals || 'Available'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Price Breakdown */}
            <aside className="details-right">
              <div className="price-breakdown-card">
                <div className="price-card-header">
                  <span className="price-icon-header">💰</span>
                  <h2 className="price-card-title">Price Breakdown</h2>
                </div>
                <div className="price-breakdown-content">
                  <div className="price-item-breakdown">
                    <div className="price-item-row">
                      <span className="price-icon-item">💵</span>
                      <span className="price-label-breakdown">Base Fare</span>
                    </div>
                    <span className="price-value-breakdown">{trip.price?.baseFare || '₦750,000'}</span>
                  </div>
                  <div className="price-item-breakdown">
                    <div className="price-item-row">
                      <span className="price-icon-item">📋</span>
                      <span className="price-label-breakdown">Taxes & Fees</span>
                    </div>
                    <span className="price-value-breakdown">{trip.price?.taxes || '₦100,000'}</span>
                  </div>
                  <div className="price-divider-breakdown"></div>
                  <div className="price-item-breakdown total">
                    <div className="price-item-row">
                      <span className="price-icon-item">💳</span>
                      <span className="price-label-breakdown">Total Paid</span>
                    </div>
                    <span className="price-value-breakdown">{trip.price?.total || '₦850,000'}</span>
                  </div>
                  <div className="payment-method-section">
                    <div className="payment-method-item">
                      <span className="payment-icon">💳</span>
                      <div className="payment-info">
                        <span className="payment-label">Payment Method</span>
                        <span className="payment-value">{trip.paymentMethod || 'Paystack'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="trip-support-section">
        <div className="page-container">
          <div className="support-banner">
            <div className="support-banner-content">
              <p className="support-banner-text">Need help with your booking?</p>
              <div className="support-banner-buttons">
                <a 
                  href="https://wa.me/2348012345678" 
                  className="support-banner-button whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp Support
                </a>
                <a 
                  href="mailto:support@symbolfly.com" 
                  className="support-banner-button email"
                >
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TripDetailsPage;








